import { Icon } from "@circle-react-shared/Icon";
import { useBoolean } from "@circle-react/hooks/utils/useBoolean";
import type { Source } from "@circle-react/types/AiSummary/Source";
import { Icon as CompassIcon } from "@circleco/compass/components/Icon";

import { t } from "@/i18n-js/instance";
import { Button } from "@/react/components/SettingsApp/CompassCompat/Button";
import { Typography } from "@/react/components/SettingsApp/CompassCompat/Typography";
import { useCircleV4 } from "@/react/hooks/useCircleV4";

interface SourcesProps {
  sources: Source[];
}

export const Sources = ({ sources }: SourcesProps) => {
  const [isOpen, toggleIsOpen] = useBoolean(false);
  const { isCircleV4 } = useCircleV4();
  const sourcesCount = sources.length;

  if (!isOpen) {
    const sourcesText =
      sourcesCount === 1
        ? t("ai_summary.sources.one")
        : t("ai_summary.sources.other", {
            count: sourcesCount,
          });
    return (
      <Button
        variant="secondary-light"
        type="button"
        onClick={toggleIsOpen}
        small
      >
        <span className="flex items-center gap-2">
          {isCircleV4 ? (
            <CompassIcon name="list-ai" size="sm" />
          ) : (
            <Icon type="16-ai-sources" size={16} />
          )}
          {sourcesText}
        </span>
      </Button>
    );
  }

  return (
    <div className="flex flex-col items-start gap-3">
      <div className="flex flex-col gap-2">
        {sources.map((source, index) => (
          <div
            key={`source-${source.url}`}
            className="grid grid-cols-[1.5rem_1fr] items-start"
          >
            <span className="text-default text-xs bg-tertiary mt-0.5 flex size-4 items-center justify-center rounded-full">
              {index + 1}
            </span>
            <a
              href={source.url}
              target="_blank"
              rel="noreferrer"
              className="text-default hover:text-default text-sm font-normal underline"
            >
              <Typography.BodyXs>
                {source.title || source.url}
              </Typography.BodyXs>
            </a>
          </div>
        ))}
      </div>
      <Button
        variant="secondary-light"
        type="button"
        onClick={toggleIsOpen}
        small
        className="rounded-full"
      >
        <span className="flex items-center gap-2">
          {isCircleV4 ? (
            <CompassIcon name="list-ai" size="sm" />
          ) : (
            <Icon type="16-ai-sources" size={16} />
          )}
          {t("ai_summary.sources.hide")}
        </span>
      </Button>
    </div>
  );
};
